import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { RefreshCw, CircleCheck, CircleAlert } from "lucide-react";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function SyncHealthBadge() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sync-status");
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    toast.info("Syncing live data…", { description: "Pulling fresh results & fixtures." });
    try {
      const { data } = await api.post("/admin/sync-live");
      const errs = (data.results || []).filter((r) => r.error).length;
      toast.success("Live sync complete", {
        description: errs ? `${data.results.length - errs} ok · ${errs} failed (rate limit)` : `${data.results.length} competitions updated`,
      });
      await load();
    } catch {
      toast.error("Sync failed", { description: "The data provider may be rate-limiting. Try again shortly." });
    } finally {
      setSyncing(false);
    }
  };

  const errCount = (status?.results || []).filter((r) => r.error).length;
  const ok = status?.last_run && errCount === 0;

  return (
    <button
      onClick={runSync}
      disabled={syncing}
      data-testid="sync-health-badge"
      title={status?.last_run ? `Last live sync ${relTime(status.last_run)} (${status.trigger || "?"})\nClick to sync now` : "No sync yet — click to sync now"}
      className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 border border-border text-[10px] uppercase tracking-wider text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors disabled:opacity-60"
    >
      {syncing ? (
        <RefreshCw size={12} className="animate-spin text-primary" />
      ) : ok ? (
        <CircleCheck size={12} className="text-[hsl(var(--pitch-green))]" />
      ) : (
        <CircleAlert size={12} className={status?.last_run ? "text-[hsl(var(--stadium-yellow))]" : "text-muted-foreground"} />
      )}
      <span data-testid="sync-health-time">{syncing ? "Syncing…" : `Data · ${relTime(status?.last_run)}`}</span>
    </button>
  );
}
